import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  getSupportedBrowserTimeZone,
  TIME_ZONES,
  timeZoneLabel,
} from "../../utils/timeZones";

// Shows the business time zone inside the "Set your business details"
// onboarding step so a pro can confirm the zone detected at signup, or pick
// another one without leaving the checklist.
const BusinessTimeZoneConfirm = ({
  timeZone,
  confirmed = false,
  disabled = false,
  onConfirm,
  onChange,
}) => {
  const browserTimeZone = getSupportedBrowserTimeZone();
  const [isChanging, setIsChanging] = useState(false);
  const [draft, setDraft] = useState(timeZone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const startChanging = () => {
    setDraft(browserTimeZone && browserTimeZone !== timeZone ? browserTimeZone : timeZone);
    setError("");
    setIsChanging(true);
  };

  const save = async () => {
    if (draft === timeZone) {
      onConfirm?.();
      setIsChanging(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onChange?.(draft);
      setIsChanging(false);
    } catch (saveError) {
      setError(saveError?.message || "We couldn't save your time zone. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (isChanging) {
    return (
      <div className="pro-onboarding-card__time-zone">
        <label className="pro-onboarding-card__time-zone-field">
          <span>Business time zone</span>
          <select
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={saving}
          >
            {!TIME_ZONES.some(([value]) => value === draft) && (
              <option value={draft}>{draft}</option>
            )}
            {TIME_ZONES.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <div className="pro-onboarding-card__time-zone-actions">
          <button
            type="button"
            className="pro-onboarding-card__step-action"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save time zone"}
          </button>
          <button
            type="button"
            className="pro-onboarding-card__step-action pro-onboarding-card__step-action--secondary"
            onClick={() => setIsChanging(false)}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
        {error && (
          <p className="pro-onboarding-card__time-zone-error" role="alert">{error}</p>
        )}
      </div>
    );
  }

  const differsFromBrowser = Boolean(browserTimeZone && browserTimeZone !== timeZone);

  return (
    <div className="pro-onboarding-card__time-zone">
      <p className="pro-onboarding-card__time-zone-summary">
        Time zone: <strong>{timeZoneLabel(timeZone)}</strong>
        {confirmed ? " · Confirmed" : ""}
      </p>
      {!confirmed && (
        <p className="pro-onboarding-card__time-zone-hint">
          {differsFromBrowser
            ? `This device is set to ${timeZoneLabel(browserTimeZone)}. `
            : browserTimeZone
              ? "Detected from your device. "
              : ""}
          Bookings, reminders, and cutoffs use this time zone.
        </p>
      )}
      <div className="pro-onboarding-card__time-zone-actions">
        {!confirmed && (
          <button
            type="button"
            className="pro-onboarding-card__step-action"
            onClick={onConfirm}
            disabled={disabled}
          >
            Confirm time zone
          </button>
        )}
        <button
          type="button"
          className="pro-onboarding-card__step-action pro-onboarding-card__step-action--secondary"
          onClick={startChanging}
          disabled={disabled}
        >
          Change time zone
        </button>
      </div>
    </div>
  );
};

BusinessTimeZoneConfirm.propTypes = {
  timeZone: PropTypes.string.isRequired,
  confirmed: PropTypes.bool,
  disabled: PropTypes.bool,
  onConfirm: PropTypes.func,
  onChange: PropTypes.func,
};

export default BusinessTimeZoneConfirm;
